<template>
  <div class="settings">
    <VPBreadcrumbs :elements="[{ text: 'Team', to: parentRoute }, { text: userAccountName }]" />
    <Form
      v-if="selectedUserAccount"
      :form-id="formId"
      :initial-values="selectedUserAccount"
    >
      <UserAccountFields
        :roles="computedTeamRoles"
        :disabled="true"
      />
      <FormActionButtons
        :show-save="isFormDirty()"
        :show-cancel="isFormDirty()"
        @save="handleSave"
        @cancel="onCancel"
      />
    </Form>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { useUserAccountsStore } from '@/stores/user-accounts';
import { useTeamRolesStore } from '@/stores/team-roles';
import UserAccountFields from './UserAccountFields.vue';
import { Form, useFormsStoreComposable } from 'vuniform';
import { useRootStore } from '@/stores/root';
import { useToast } from 'vue-toastification';
import { useRouter } from 'vue-router';
import type { SaveUserAccountMutationInput, UserAccountAttributes } from '../../generated/graphql';
import VPBreadcrumbs from '@/components/VPBreadcrumbs.vue';
import FormActionButtons from '@/components/form/FormActionButtons.vue';

const userAccountStore = useUserAccountsStore();
const teamRolesStore = useTeamRolesStore();
const route = useRoute();
const toast = useToast();
const router = useRouter();
const parentRoute = { name: 'UserAccountIndex' };

const rootStore = useRootStore();
const { formId, formsStore, formGetValues, isFormDirty } = useFormsStoreComposable('edit-user-account-form');

const userId = parseInt(route.params.userId as string, 10);

userAccountStore.fetchAllUsersAccounts(rootStore.currentAccountId, userId);
teamRolesStore.fetchAllTeamRoles(rootStore.currentAccountId);

const computedTeamRoles = computed(() => {
  return teamRolesStore.teamRoles.map(role => ({ name: role.name, value: role.id }));
});

const selectedUserAccount = computed(() => {
  return userAccountStore.usersAccounts.find(userAccount => userAccount.user?.id === userId);
});

const userAccountName = computed(() => selectedUserAccount.value?.user?.name || '');

const handleSave = async () => {
  const values = formGetValues<UserAccountAttributes>();
  const payload: SaveUserAccountMutationInput = {
    clientMutationId: formId,
    userAccount: {
      ...values,
      accountId: rootStore.currentAccountId,
    },
  };
  const { result: updatedUserAccount, errors } = await userAccountStore.saveUser(payload);

  if (errors) {
    formsStore.SET_ERRORS({ requesterId: formId, errors });
    return;
  }

  if (!updatedUserAccount) {
    return;
  }

  toast.success(`User account '${updatedUserAccount.user?.name}' updated successfully!`);
  router.push(parentRoute);
};
const onCancel = () => formsStore.RESET_FIELD_VALUES(formId);
</script>
